/**
 * THE DENORMALIZED SHIFT COLUMNS, AND WHAT THEY ARE ALLOWED TO MEAN.
 *
 * `technicians.hours` is the canonical per-day schedule and the only thing that
 * may answer a question containing a DATE. `technicians.shift_start` /
 * `shift_end` are a flat weekly summary kept beside it for readers that predate
 * the per-day map.
 *
 * This lived inline inside `TechnicianForm`'s save handler, which had two
 * consequences worth undoing: it could not be tested, and nothing it produced
 * carried any indication of what it was. A caller reading `tech.shift_end` saw
 * a plausible "16:00" and had no way to know it was a MODE across the week.
 *
 * Joe D is the case that made this concrete. Monday 07:30–22:00, Tuesday to
 * Friday 07:30–16:00: four 16:00 closes outvote one 22:00 close, `shift_end`
 * becomes 16:00, and every surface that showed it told the shop Joe goes home
 * at four — on the one day he does not.
 *
 * So the value is unchanged (it is still what the column has always held, and
 * rewriting it would change data for every technician in the fleet) and it is
 * now LABELLED. `kind: 'typical_week'` is the whole point: a summary of a week,
 * never a statement about a day.
 *
 * For anything dated, use `getTechDayHours(tech, dateStr)`.
 */

export type ShiftEnvelopeKind = 'typical_week';

export interface LegacyShiftEnvelope {
  /** Earliest opening time across every day the technician works. */
  shift_start: string;
  /**
   * The MOST FREQUENT closing time, preferring Mon–Fri when any weekday is
   * present, so one late Sunday does not claim the whole week runs to 23:00.
   * Ties break toward the later time.
   */
  shift_end: string;
  /**
   * What this describes. Present so a consumer cannot read the pair as a day's
   * schedule without first ignoring a field that says it is not one.
   */
  kind: ShiftEnvelopeKind;
}

type HoursMap = Record<string, { open?: string; close?: string } | undefined>;

const toMinutes = (t: string): number => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
};

const normalize = (t?: string): string => (t ? t.slice(0, 5) : '');

/**
 * Derive the flat compatibility columns from the canonical per-day map.
 *
 * Behaviour is deliberately IDENTICAL to what `TechnicianForm` has always
 * written — this is an extraction, not a change. Changing the value would
 * silently re-write the summary for every technician in the fleet, and the
 * defect was never the value: it was that consumers treated a weekly summary as
 * a dated fact.
 */
export function deriveLegacyShiftEnvelope(
  hours: HoursMap | null | undefined,
  fallback?: { shift_start?: string; shift_end?: string },
): LegacyShiftEnvelope {
  const entries = Object.entries(hours || {}) as Array<
    [string, { open?: string; close?: string } | undefined]
  >;

  // The sentinel the schema expects when a technician has no per-day schedule
  // at all. Not a fabricated 08:00–17:00: an invented shift is worse than an
  // obviously empty one, because it looks like an answer.
  if (entries.length === 0) {
    return {
      shift_start: fallback?.shift_start ?? '00:00',
      shift_end: fallback?.shift_end ?? '00:00',
      kind: 'typical_week',
    };
  }

  const weekdaySet = new Set([1, 2, 3, 4, 5]);
  const opens = entries.map(([, e]) => e?.open).filter(Boolean) as string[];

  const weekdayCloses = entries
    .filter(([day]) => weekdaySet.has(Number(day)))
    .map(([, e]) => e?.close)
    .filter(Boolean) as string[];
  const allCloses = entries.map(([, e]) => e?.close).filter(Boolean) as string[];
  const closes = weekdayCloses.length ? weekdayCloses : allCloses;

  const earliestOpen = opens.length
    ? opens.reduce((a, b) => (toMinutes(a) <= toMinutes(b) ? a : b))
    : fallback?.shift_start;

  const closeCounts = closes.reduce<Record<string, number>>((acc, t) => {
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});
  const modeClose = Object.keys(closeCounts).sort((a, b) => {
    if (closeCounts[b] === closeCounts[a]) return toMinutes(b) - toMinutes(a);
    return closeCounts[b] - closeCounts[a];
  })[0];
  const representativeClose =
    modeClose ||
    (closes.length
      ? closes.reduce((a, b) => (toMinutes(a) >= toMinutes(b) ? a : b))
      : fallback?.shift_end);

  return {
    shift_start: normalize(earliestOpen) || '00:00',
    shift_end: normalize(representativeClose) || '00:00',
    kind: 'typical_week',
  };
}

/**
 * Does the flat summary actually describe this day?
 *
 * Useful for a surface that wants to show the flat pair but must not present it
 * as today's schedule when it disagrees with the canonical map.
 */
export function envelopeMatchesDay(
  envelope: Pick<LegacyShiftEnvelope, 'shift_start' | 'shift_end'>,
  dayHours: { open: string; close: string } | null,
): boolean {
  if (!dayHours) return false;
  return (
    normalize(envelope.shift_start) === normalize(dayHours.open) &&
    normalize(envelope.shift_end) === normalize(dayHours.close)
  );
}
